<script setup name="TvbHomeSmallBannerTemplateThree">
import { ref, inject } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { FreeMode } from 'swiper/modules'
import { action } from './../../../../../../utils/gtm'

const swiperRef = ref()
const size = inject('size')

const props = defineProps({
  info: {
    type: Object,
    default: null
  },
  big: {
    type: Object,
    default: () => {}
  },
  list: {
    type: Array,
    default: () => []
  },
  linkTarget: {
    type: Number,
    default: 1
  }
})

const slidePrev = () => swiperRef?.value?.$el?.swiper?.slidePrev()
const slideNext = () => swiperRef?.value?.$el?.swiper?.slideNext()
const tracking = (item) => {
  action({
    page_title: props.info.title,
    screen_class: item.title || item.name,
    screenID: 'S001',
    eventCategory: 'home_banner',
    eventLabel: props.info.api,
    eventID: item.id || item.post_id,
    screenLabel: item.jump_url || item.link
  })
}

defineExpose({ slidePrev, slideNext })
</script>

<template>
  <main class="tvb-home-small-banner-main" :class="[size]">
    <TvbRouterLink
      :to="big.jump_url"
      :link-rel="big.link_rel"
      :link-target="big.link_target || linkTarget"
      class="tvb-home-small-banner-big tvb-hairline--surround"
      :title="big.title || big.name"
      @click="tracking(big)"
    >
      <TvbImage
        class="tvb-home-small-banner-big-image"
        transform
        backgroundc="#f0f1f2"
        :src="big.image || big.thumbnails"
        :alt="big.title || big.name"
        :is-video="!!big?.video_url"
        :border-radius="['s', 'xs'].includes(size) ? 6 : 8"
      />
      <h3 class="tvb-home-small-banner-big-title">
        <span>{{ big.title || big.name }}</span>
      </h3>
      <p class="tvb-home-small-banner-big-description" :title="big.description || big.caption_en">
        {{ big.description || big.caption_en || '&nbsp;' }}
      </p>
    </TvbRouterLink>
    <div class="tvb-home-small-banner-small">
      <Swiper
        ref="swiperRef"
        :key="size"
        :loop="!['s', 'xs'].includes(size)"
        :slides-per-view="['s', 'xs'].includes(size) ? 2.3529 : 1"
        :space-between="['s', 'xs'].includes(size) ? 10 : 16"
        :slides-offset-after="['s', 'xs'].includes(size) ? 12 : 0"
        :slides-offset-before="['s', 'xs'].includes(size) ? 12 : 0"
        :modules="[FreeMode]"
        :free-mode="{
          enabled: ['s', 'xs'].includes(size),
          momentum: false,
          minimumVelocity: 10,
          momentumBounceRatio: 0.01,
          momentumRatio: 0.01,
          momentumVelocityRatio: 0.01
        }"
        :speed="500"
      >
        <template v-if="['s', 'xs'].includes(size)">
          <SwiperSlide v-for="(item, index) in list" :key="index">
            <TvbRouterLink
              :to="item.jump_url"
              :link-rel="item.link_rel"
              :link-target="item.link_target || linkTarget"
              class="tvb-home-small-banner-small-item"
              :title="item.title"
              @click="tracking(item)"
            >
              <TvbImage
                class="tvb-home-small-banner-small-image"
                :src="item.image || item.thumbnails"
                :alt="item.title || item.name"
                :play-icon="{ size: { xl: 48, l: 48, m: 48, s: 40, xs: 40 } }"
                :is-video="!!item?.video_url"
                :border-radius="['s', 'xs'].includes(size) ? 6 : 8"
                border
              />
              <h3 class="tvb-home-small-banner-small-title">
                <span>{{ item.title || item.name }}</span>
              </h3>
            </TvbRouterLink>
          </SwiperSlide>
        </template>
        <template v-else>
          <SwiperSlide
            v-for="(_index, index) in list.length / 4"
            :key="_index"
            class="swiper-slide-4"
          >
            <TvbRouterLink
              v-for="(item, indexs) in list.slice(index * 4, (index + 1) * 4)"
              :to="item.jump_url"
              :link-rel="item.link_rel"
              :link-target="item.link_target || linkTarget"
              class="tvb-home-small-banner-small-item"
              :title="item.title"
              :key="indexs"
              @click="tracking(item)"
            >
              <TvbImage
                class="tvb-home-small-banner-small-image"
                :src="item.image || item.thumbnails"
                :alt="item.title || item.name"
                :play-icon="{ size: { xl: 48, l: 48, m: 48, s: 32, xs: 32 } }"
                :is-video="!!item?.video_url"
                transform
                border
                :border-radius="['s', 'xs'].includes(size) ? 6 : 8"
              />
              <h3 class="tvb-home-small-banner-small-title">
                <span>{{ item.title || item.name }}</span>
              </h3>
            </TvbRouterLink>
          </SwiperSlide>
        </template>
      </Swiper>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.tvb-home-small-banner-main {
  display: flex;
  width: 100%;
  margin-top: 16px;
  height: max-content;
  .tvb-home-small-banner-big {
    text-decoration: none;
    width: calc((100% - 16px) / 2);
    margin-right: 8px;
    background: #f8f9fa;
    border-radius: 8px;

    --tvb-hairline-border-radius: calc(2 * 8px);
    // border: 0.5px solid rgba(163, 178, 188, 0.35);
    height: max-content;
    overflow: hidden;
    @include safari-overflow-hidden();

    .tvb-home-small-banner-big-image {
      aspect-ratio: 1/0.5613207547169812;
      width: 100%;
      height: auto;
      border-radius: 8px;
      overflow: hidden;
    }

    .tvb-home-small-banner-big-title {
      font-size: 22px;
      font-weight: 500;
      color: #333333;
      line-height: 28px;
      padding: 12px 16px 0;
      word-break: break-all;
      letter-spacing: 0.5px;
      @include line-text-ellipsis(1);
      span {
        transition: color 0.15s linear;
        &:hover {
          color: rgba(0, 98, 163, 1);
        }
      }
    }

    .tvb-home-small-banner-big-description {
      font-size: 18px;
      font-weight: 400;
      color: #818d95;
      line-height: 24px;
      padding: 4px 16px 12px;
      word-break: break-all;
      white-space: nowrap;
      letter-spacing: 0.5px;
      @include line-text-ellipsis(1);
      display: block;
    }
  }
  .tvb-home-small-banner-small {
    width: calc((100% - 16px) / 2);
    margin-left: 8px;
    height: max-content;
    .swiper-slide-4 {
      display: flex;
      flex-wrap: wrap;
    }
    .tvb-home-small-banner-small-item {
      display: inline-block;
      width: calc((100% - 16px) / 2);
      text-decoration: none;

      .tvb-home-small-banner-small-image {
        width: 100%;
        aspect-ratio: 1/0.5625;
        height: auto;
        border-radius: 8px;
        overflow: hidden;
        @include safari-overflow-hidden();
      }

      .tvb-home-small-banner-small-title {
        margin-top: 10px;
        font-size: 20px;
        font-weight: 500;
        color: #333333;
        line-height: 26px;
        letter-spacing: 0.5px;
        @include line-text-ellipsis(1);
        span {
          transition: color 0.15s linear;
          &:hover {
            color: rgba(0, 98, 163, 1);
          }
        }
      }

      &:nth-child(1) {
        margin-right: 8px;
        margin-bottom: 8px;
      }
      &:nth-child(2) {
        margin-left: 8px;
        margin-bottom: 8px;
      }
      &:nth-child(3) {
        margin-right: 8px;
        margin-top: 8px;
      }
      &:nth-child(4) {
        margin-left: 8px;
        margin-top: 8px;
      }
    }
  }

  &.s,
  &.xs {
    flex-direction: column;
    margin-top: 10px;

    .tvb-home-small-banner-big {
      width: calc(100% - 24px);
      margin: 0;
      margin: 0 12px 0;
      border-radius: 6px;

      --tvb-hairline-border-radius: calc(2 * 6px);
      .tvb-home-small-banner-big-image {
        border-radius: 5px;
        width: 100%;
        aspect-ratio: 1 / 0.5628415300546448;
        @supports not (aspect-ratio: 1 / 0.5628415300546448) {
          height: 196px;
        }
      }

      .tvb-home-small-banner-big-title {
        font-size: 16px;
        font-weight: 400;
        color: #333333;
        line-height: 22px;
        padding: 8px 10px 0;
      }

      .tvb-home-small-banner-big-description {
        font-size: 13px;
        font-weight: 400;
        color: #818d95;
        line-height: 18px;
        padding: 2px 10px 10px;
        letter-spacing: 0;
      }
    }

    .tvb-home-small-banner-small {
      width: 100%;
      margin: 0;
      margin-top: 10px;
      .tvb-home-small-banner-small-item {
        width: 100%;
        margin: 0;
        display: block;

        .tvb-home-small-banner-small-image {
          align-items: 0.5625 / 1;
          border-radius: 6px;
          overflow: hidden;

          @include safari-overflow-hidden();
          @supports not (aspect-ratio: 1 / 0.5628415300546448) {
            height: 90px;
          }
        }
        .tvb-home-small-banner-small-title {
          margin-top: 6px;
          font-size: 16px;
          font-weight: 400;
          color: #333333;
          line-height: 22px;
          span {
            transition: color 0.15s linear;
            &:hover {
              color: rgba(0, 98, 163, 1);
            }
          }
        }
      }
    }
  }

  &.s {
    .tvb-home-small-banner-big {
      width: calc(100% - 32px);
      margin: 0;
      margin: 0 16px 0;
    }
  }
}
</style>
